import { useState } from 'react';
import { CheckCircle2, KeyRound, Lock, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { authApi } from '@/lib/api';
import type { AuthUser } from '@/types';

interface AuthPageProps {
  onAuthenticated: (token: string, user: AuthUser) => void;
}

type AuthMode = 'login' | 'register' | 'forgot' | 'reset';

export function AuthPage({ onAuthenticated }: AuthPageProps) {
  const [mode, setMode] = useState<AuthMode>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetFeedback = () => {
    setMessage('');
    setError('');
  };

  const handleLogin = async () => {
    setIsSubmitting(true);
    resetFeedback();

    try {
      const result = await authApi.login({ email, password });
      onAuthenticated(result.token, result.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to log in');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async () => {
    setIsSubmitting(true);
    resetFeedback();

    try {
      const result = await authApi.register({ name, email, password });
      setMessage(result.message);
      setMode('login');
      setPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to register');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    setIsSubmitting(true);
    resetFeedback();

    try {
      const result = await authApi.forgotPassword(email);
      setMessage(result.message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to start password reset');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    setIsSubmitting(true);
    resetFeedback();

    try {
      await authApi.resetPassword({ resetToken, password });
      setMessage('Password updated. You can sign in with the new password.');
      setMode('login');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to reset password');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (mode === 'login') void handleLogin();
    if (mode === 'register') void handleRegister();
    if (mode === 'forgot') void handleForgotPassword();
    if (mode === 'reset') void handleResetPassword();
  };

  const title = {
    login: 'Sign in',
    register: 'Request client access',
    forgot: 'Forgot password',
    reset: 'Reset password',
  }[mode];

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto flex max-w-5xl flex-col gap-8 lg:grid lg:grid-cols-[1fr_420px] lg:items-center">
        <section>
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-600 text-xl font-bold text-white">
              O
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">OrbitCrew Dashboard</h1>
              <p className="text-sm text-slate-500">Client portal with admin-approved access.</p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            {[
              { icon: UserPlus, title: 'Register', copy: 'Clients submit an access request first.' },
              { icon: CheckCircle2, title: 'Admin approval', copy: 'Admin reviews and approves accounts.' },
              { icon: Lock, title: 'Controlled access', copy: 'Approved users can access project features.' },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="rounded-lg border border-slate-200 bg-white p-4">
                  <Icon className="mb-3 h-5 w-5 text-blue-600" />
                  <h2 className="font-semibold text-slate-900">{item.title}</h2>
                  <p className="mt-1 text-sm text-slate-500">{item.copy}</p>
                </div>
              );
            })}
          </div>
        </section>

        <form onSubmit={submit} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-slate-900">{title}</h2>
            <p className="mt-1 text-sm text-slate-500">
              {mode === 'register'
                ? 'Your account will stay pending until an admin approves it.'
                : 'Use an approved client or admin account.'}
            </p>
          </div>

          <div className="space-y-4">
            {mode === 'register' && (
              <Input
                label="Name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Agency contact name"
                required
              />
            )}
            {mode !== 'reset' && (
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="client@example.com"
                required
              />
            )}
            {mode === 'reset' && (
              <Input
                label="Reset code"
                value={resetToken}
                onChange={(event) => setResetToken(event.target.value)}
                placeholder="Paste reset code"
                required
              />
            )}
            {mode !== 'forgot' && (
              <Input
                label={mode === 'reset' ? 'New password' : 'Password'}
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Minimum 8 characters"
                required
              />
            )}
          </div>

          {message && (
            <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
              {message}
            </div>
          )}
          {error && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <Button
            type="submit"
            className="mt-6 w-full"
            isLoading={isSubmitting}
            leftIcon={mode === 'forgot' || mode === 'reset' ? <KeyRound className="h-4 w-4" /> : undefined}
          >
            {mode === 'login' && 'Sign in'}
            {mode === 'register' && 'Submit registration'}
            {mode === 'forgot' && 'Submit/check reset request'}
            {mode === 'reset' && 'Update password'}
          </Button>

          <div className="mt-4 flex flex-wrap justify-center gap-3 text-sm">
            {mode !== 'login' && (
              <button type="button" onClick={() => setMode('login')} className="font-medium text-blue-600">
                Back to login
              </button>
            )}
            {mode !== 'register' && (
              <button type="button" onClick={() => setMode('register')} className="font-medium text-blue-600">
                Register
              </button>
            )}
            {mode !== 'forgot' && mode !== 'reset' && (
              <button type="button" onClick={() => setMode('forgot')} className="font-medium text-blue-600">
                Forgot password?
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
